/**
 * EverydayOS Logger
 *
 * Production-safe logging wrapper:
 * - debug / log / info are no-ops in production
 * - warn / error always emit
 * - error() forwards to a registered reporter (e.g. LogRocket) in production
 *
 * Usage:
 *   import { logger } from '@everyday-os/logger';
 *   logger.log('Component mounted', { id });
 *   logger.error('Firestore write failed', err);
 *
 * Wire up LogRocket (in main.tsx, after LogRocket.init()):
 *   logger.setErrorReporter((msg, err) => LogRocket.captureException(err as Error, { extra: { msg } }));
 */

type ErrorReporter = (message: string, error?: unknown) => void;

const isProd = (): boolean => {
  try {
    return (import.meta as any).env?.MODE === 'production';
  } catch {
    return false;
  }
};

let _errorReporter: ErrorReporter | null = null;

export const logger = {
  /** Register a production error reporter (e.g. LogRocket.captureException). */
  setErrorReporter(fn: ErrorReporter): void {
    _errorReporter = fn;
  },

  /** Development-only. Silent in production. */
  debug(...args: unknown[]): void {
    if (!isProd()) console.debug('[EverydayOS]', ...args);
  },

  /** Development-only. Silent in production. */
  log(...args: unknown[]): void {
    if (!isProd()) console.log('[EverydayOS]', ...args);
  },

  /** Development-only. Silent in production. */
  info(...args: unknown[]): void {
    if (!isProd()) console.info('[EverydayOS]', ...args);
  },

  /** Always emits. Use for recoverable unexpected states. */
  warn(...args: unknown[]): void {
    console.warn('[EverydayOS]', ...args);
  },

  /** Always emits. Forwards to error reporter in production. */
  error(message: string, error?: unknown): void {
    console.error('[EverydayOS]', message, error);
    if (isProd() && _errorReporter) {
      try {
        _errorReporter(message, error);
      } catch {
        // Reporter itself must not throw
      }
    }
  },
};

/** @deprecated Use `logger` instead. Kept for backwards compatibility. */
export const formatLog = (message: string, data?: unknown) => ({
  message,
  data,
  timestamp: new Date().toISOString(),
  env: (() => { try { return (import.meta as any).env?.MODE; } catch { return 'unknown'; } })(),
});
